'use client';

import { useState } from 'react';
import { TranslatedText } from '@/components/shared';
import { Link, useRouter } from '@/i18n/routing';
import Eye from '@fluentui/svg-icons/icons/eye_20_regular.svg';
import EyeOff from '@fluentui/svg-icons/icons/eye_off_20_regular.svg';
import { useTranslations } from 'next-intl';
import { validateLogin } from '@/app/actions/auth';
import LoadingSpinner from '@/app/components/shared/LoadingSpinner';
import { createSessionAndSendSms } from '@/app/lib/createSession';
import { decryptPayload } from '@/app/lib/phoneCipher';
import { useAuth } from '@/app/providers/auth-provider';
import TextField from './TextField';

export default function LoginForm() {
  const t = useTranslations('Login');
  const [showPassword, setShowPassword] = useState(false);
  const { setUserData } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setError(null);
    setIsSubmitting(true);

    const formData = new FormData(form);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    if (!email || !password) {
      setError('البريد الإلكتروني وكلمة المرور مطلوبة');
      setIsSubmitting(false);
      return;
    }

    try {
      const validationResult = await validateLogin(email, password);

      if (validationResult.error) {
        setError(validationResult.error);
        setIsSubmitting(false);
        return;
      }

      const result = await createSessionAndSendSms(email);

      if (!result.success) {
        setError(result.error || 'حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى');
        setIsSubmitting(false);
        return;
      }

      const decryptedMobileNumber = result.mobileNumberEncrypted
        ? decryptPayload(result.mobileNumberEncrypted)
        : null;

      setUserData({
        email,
        password,
        mobileNumber: decryptedMobileNumber || result.mobileNumber || '',
      });

      router.push(
        `/verify-login${
          result.verificationCode ? `?code=${result.verificationCode}` : ''
        }`,
      );
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message;

      setError(
        Array.isArray(backendMessage)
          ? backendMessage[0]?.value || 'حدث خطأ غير متوقع'
          : typeof backendMessage === 'string'
            ? backendMessage
            : typeof backendMessage === 'object' && backendMessage?.message
              ? backendMessage.message
              : 'حدث خطأ غير متوقع',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="group flex w-full max-w-[406px] flex-col gap-6"
    >
      <p className="text-center text-lg font-medium leading-8">{t('title')}</p>
      <div className="flex w-full flex-col gap-4">
        <TextField name="email" type="email" label={t('email')} required />
        <div className="flex flex-col gap-2">
          <TextField
            name="password"
            label={t('password')}
            type={showPassword ? 'text' : 'password'}
            required
            trailing={
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="flex focus:outline-none"
              >
                {showPassword ? <Eye /> : <EyeOff />}
              </button>
            }
          />
          <Link
            href="/forgot-password"
            className="text-end text-sm font-medium leading-6 text-blue-600 hover:underline"
          >
            {t('forgotPassword')}
          </Link>
        </div>
      </div>

      {error && (
        <p className="text-center text-sm font-normal text-danger">{error}</p>
      )}
      {/* {state?.error && (
        <p className="text-center text-sm font-normal text-danger">
          {state?.error}
        </p>
      )} */}
      {/* {isPending ? (
        <div className="bg-surface/50 absolute inset-0 z-50 grid size-full place-items-center backdrop-blur-lg">
          <ActivityIndicator />
        </div>
      ) : state?.error ? (
        <p className="text-center text-sm font-normal text-danger">
          {state?.error}
        </p>
      ) : null} */}

      <div className="flex flex-col gap-6">
        <button
          type="submit"
          disabled={isSubmitting}
          className="h-12 w-full rounded-xl bg-blue-600 font-medium text-on-primary group-invalid:bg-gray-200 group-invalid:text-gray-400"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner className="h-4 w-4 border-[2px] !border-gray-400 !border-r-white" />
              {t('signing')}
            </span>
          ) : (
            t('signin')
          )}
        </button>
        <p className="text-sm font-normal leading-6">
          <TranslatedText
            text="Login.register"
            richValues={{
              a: (chunks) => (
                <span
                  className="cursor-pointer font-medium text-blue-600 hover:underline"
                  onClick={() => router.push('/register')}
                >
                  {chunks}
                </span>
              ),
            }}
          />

          {/* {t.rich("register", {
            a: (chunks) => (
              <Link
                href="https://testing-b2b.abyantech.com/ar/onboarding"
                className="font-medium text-blue-600 hover:underline"
              >
                {chunks}
              </Link>
            ),
          })} */}
        </p>
      </div>
    </form>
  );
}
